import type { PolicyEvent, PolicyInputGroup } from "./inputs";
import { inputGroupKnownBefore, policyInputGroups } from "./inputs";
import type { ReturnPolicySnapshotSettings } from "./snapshot-settings";

export type ReturnPolicySettingName = keyof ReturnPolicySnapshotSettings;

/** Whether a setting resolves once per returned item or once per return. */
export type SettingLevel = "item" | "return";

/** `decidedBy` is set only where the portal must settle the value before some inputs exist. Every other setting may read anything. */
export type SettingDefinition = { decidedBy?: PolicyEvent } & (
  | { shape: "plain" }
  | { shape: "first_match"; level: SettingLevel }
  | { shape: "every_match"; level: SettingLevel }
  /** One first-match setting per compensation method, all decided before the method is picked. */
  | { shape: "per_method_first_match"; level: "item" }
  /** `questions`: each question carries its own `when` and is decided when it is asked. */
  | { shape: "questions"; level: "item" }
);

export const returnPolicySettingRegistry = {
  return_window: {
    shape: "first_match",
    level: "item",
    decidedBy: "select_items",
  },
  accepted_countries: { shape: "plain" },
  excluded_products: { shape: "plain" },
  reject_return_rules: { shape: "every_match", level: "item" },
  block_return_rules: { shape: "every_match", level: "item" },
  return_methods_offered: {
    shape: "first_match",
    level: "item",
    decidedBy: "pick_method",
  },
  store_credit_type: { shape: "plain" },
  exchange_types_offered: {
    shape: "first_match",
    level: "item",
    decidedBy: "pick_method",
  },
  exchange_agent_settings: { shape: "first_match", level: "item" },
  exchange_order_shipping_payer: { shape: "first_match", level: "return" },
  tags_to_add_to_exchange_order: { shape: "every_match", level: "return" },
  exchange_order_shipping_line_name: { shape: "first_match", level: "return" },
  exchange_order_shipping_rates: { shape: "first_match", level: "return" },
  hide_tagged_products_from_exchanges: { shape: "plain" },
  carry_over_line_item_properties: { shape: "first_match", level: "item" },
  carry_over_line_item_properties_on_add_on_accept: {
    shape: "first_match",
    level: "item",
  },
  exchange_add_on_offer: { shape: "first_match", level: "item" },
  allow_unpublished_replacement: { shape: "first_match", level: "item" },
  item_adjustments_by_method: {
    shape: "per_method_first_match",
    level: "item",
    decidedBy: "pick_method",
  },
  use_adjustment_function_by_method: {
    shape: "per_method_first_match",
    level: "item",
    decidedBy: "pick_method",
  },
  return_level_adjustments: { shape: "every_match", level: "return" },
  use_it_or_lose_it_bonus: { shape: "plain" },
  keep_bonus_on_advanced_exchange: { shape: "plain" },
  shop_now_bonus_eligible: { shape: "plain" },
  apply_adjustment_as_discount_code: { shape: "first_match", level: "return" },
  adjustment_discount_title: { shape: "plain" },
  waive_refund_store_credit_below_amount: { shape: "plain" },
  dynamic_bundle_adjustment: { shape: "plain" },
  discount_code_name_format: { shape: "first_match", level: "return" },
  return_shipping_fee: { shape: "first_match", level: "item" },
  require_send_back: { shape: "first_match", level: "item" },
  require_send_back_override: { shape: "first_match", level: "return" },
  return_label_provider: { shape: "first_match", level: "item" },
  return_label_upcharge: { shape: "first_match", level: "item" },
  dropoff_provider_fees: { shape: "first_match", level: "return" },
  merchant_covers_package_pickup: { shape: "first_match", level: "return" },
  package_pickup: { shape: "plain" },
  reimburse_for_final_sale: { shape: "first_match", level: "item" },
  repair_fee: { shape: "first_match", level: "item" },
  return_reasons: {
    shape: "first_match",
    level: "item",
    decidedBy: "pick_reasons",
  },
  questions: { shape: "questions", level: "item" },
  require_manual_review: { shape: "first_match", level: "return" },
  merchant_review_before_filing: { shape: "first_match", level: "return" },
  flag_for_review: { shape: "every_match", level: "return" },
  tags_to_add_to_return: { shape: "every_match", level: "return" },
  ignore_inventory_for_return_types: { shape: "plain" },
} as const satisfies Record<ReturnPolicySettingName, SettingDefinition>;

export function settingDecidedBy(
  settingName: ReturnPolicySettingName,
): PolicyEvent | undefined {
  const definition: SettingDefinition =
    returnPolicySettingRegistry[settingName];
  return definition.decidedBy;
}

/** Undefined for a plain setting, which has no rules. */
export function settingLevel(
  settingName: ReturnPolicySettingName,
): SettingLevel | undefined {
  const definition: SettingDefinition =
    returnPolicySettingRegistry[settingName];
  return definition.shape === "plain" ? undefined : definition.level;
}

/** Fact groups a rule on this setting may read; the editor offers only conditions from these. */
export function settingAllowedInputGroups(
  settingName: ReturnPolicySettingName,
): PolicyInputGroup[] {
  const decidedBy = settingDecidedBy(settingName);
  return policyInputGroups.filter(
    (group) => !decidedBy || inputGroupKnownBefore(group, decidedBy),
  );
}

function isReturnPolicySettingName(
  key: string,
): key is ReturnPolicySettingName {
  return key in returnPolicySettingRegistry;
}

export const returnPolicySettingNames: readonly ReturnPolicySettingName[] =
  Object.keys(returnPolicySettingRegistry).filter(isReturnPolicySettingName);
